import { User } from '@supabase/supabase-js';
import toast from 'react-hot-toast';
import { supabase } from './supabase';
import { apiUrl } from './api';

export interface UserProfile {
  id: string;
  role: 'admin' | 'client_manager' | 'partner' | 'viewer' | 'client_user';
  partner_id: number | null;
  client_id: number | null;
  full_name: string | null;
  avatar_url: string | null;
  is_active: boolean;
  assigned_client_ids: number[] | null;
  is_owner: boolean;
}

export interface AuthUser {
  user: User;
  profile: UserProfile;
}

// Re-export supabase client for convenience
export { supabase };

// Obtener usuario actual y perfil
export async function getCurrentUser(): Promise<AuthUser | null> {
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    return null;
  }

  const { data: profile, error: profileError } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', user.id)
    .single();

  if (profileError || !profile) {
    return null;
  }

  return { user, profile: profile as UserProfile };
}

// Login con email/password
export async function signIn(email: string, password: string) {
  return supabase.auth.signInWithPassword({ email, password });
}

// Registro con email/password
export async function signUp(email: string, password: string, fullName?: string) {
  return supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        full_name: fullName,
      },
    },
  });
}

// Logout
export async function signOut() {
  return supabase.auth.signOut();
}

// Login con magic link
export async function signInWithMagicLink(email: string) {
  return supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: `${window.location.origin}/auth/callback`,
    },
  });
}

// Verify invitation by token (uses public API)
export async function verifyInvitation(token: string) {
  try {
    const response = await fetch(apiUrl(`/api/invitations/verify?token=${token}`));
    const data = await response.json() as {
      valid?: boolean;
      email?: string;
      role?: string;
      partner_id?: number | null;
      partner_name?: string | null;
      error?: string;
    };

    if (!response.ok || data.error) {
      return { invitation: null, error: data.error || 'Invalid or expired invitation' };
    }

    // Formato compatible con el SignupPage
    return {
      invitation: {
        email: data.email!,
        role: data.role!,
        partner_id: data.partner_id ?? null,
        partners: data.partner_name ? { name: data.partner_name } : null,
      },
      error: null,
    };
  } catch {
    return { invitation: null, error: 'Error verifying invitation' };
  }
}

// Register with invitation
// Uses backend Admin API to create user without email confirmation
export async function signUpWithInvitation(
  token: string,
  email: string,
  password: string,
  fullName: string
) {
  try {
    const response = await fetch(apiUrl('/api/invitations/accept'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
        email,
        password,
        full_name: fullName,
      }),
    });

    const data = await response.json() as {
      success?: boolean;
      message?: string;
      user_id?: string;
      error?: string;
    };

    if (!response.ok || data.error) {
      return { data: null, error: { message: data.error || 'Error al crear la cuenta' } };
    }

    return { data: { success: true, message: data.message }, error: null };
  } catch {
    return { data: null, error: { message: 'Server connection error' } };
  }
}

// Escuchar cambios de auth
export function onAuthStateChange(callback: (user: User | null) => void) {
  return supabase.auth.onAuthStateChange((_event, session) => {
    callback(session?.user ?? null);
  });
}

// Get current session token
export async function getAccessToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

// Reset password
export async function resetPassword(email: string) {
  return supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/auth/reset-password`,
  });
}

// Update password
export async function updatePassword(newPassword: string) {
  return supabase.auth.updateUser({ password: newPassword });
}

/**
 * Authenticated fetch wrapper that automatically adds Authorization header.
 * Handles 401 responses by attempting a token refresh, then redirecting to login if that fails.
 */
export async function authFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = await getAccessToken();

  const headers = new Headers(options.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Resolve API URLs to the correct backend (worker URL in production)
  const resolvedUrl = url.startsWith('/api') ? apiUrl(url) : url;

  let response: Response;
  try {
    response = await fetch(resolvedUrl, { ...options, headers });
  } catch (err) {
    // Aborted requests (React Query unmount/cancel) are not network errors —
    // rethrow silently instead of showing a spurious "Connection error" toast.
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    toast.error('Connection error — check your network', { id: 'network-error' });
    throw new Error('Network request failed');
  }

  // If 401, try refreshing the session once
  if (response.status === 401 && token) {
    const { data, error } = await supabase.auth.refreshSession();
    if (!error && data.session) {
      const newHeaders = new Headers(options.headers);
      newHeaders.set('Authorization', `Bearer ${data.session.access_token}`);
      return fetch(resolvedUrl, { ...options, headers: newHeaders });
    }
    // Refresh failed — session is dead, redirect to login
    toast.error('Session expired — please log in again', { id: 'session-expired' });
    await supabase.auth.signOut();
    window.location.href = '/auth/login';
  }

  // Show toast for server errors (not for client errors which are handled by callers)
  if (response.status >= 500) {
    toast.error('Server error — please try again', { id: 'server-error' });
  }

  return response;
}

/**
 * Authenticated fetch that returns JSON
 * Throws error if response is not ok
 */
export async function authFetchJson<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await authFetch(url, options);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error((error as { error?: string }).error || `HTTP ${response.status}`);
  }

  return response.json() as Promise<T>;
}
